import {
  AlertTriangle,
  Check,
  CircleOff,
  Copy,
  KeyRound,
  RefreshCw,
  ShieldCheck,
  Users,
  Video,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components'
import {
  useApproveMeetingImport,
  useGiftAuditEvents,
  useGiftOperators,
  useIssueGiftMcpCredential,
  useMeetingImports,
  useMeetingImportApprovalTarget,
  useMeetingSources,
  useOnboardGiftOperator,
  useOffboardGiftOperator,
  useReissueGiftMcpCredential,
  useRejectMeetingImport,
  useRevokeGiftMcpCredential,
  useSyncMeetingImports,
  useTestMeetingSource,
  useUpdateMeetingSource,
} from '@/hooks/useGiftOperations'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import {
  PRESET_DESCRIPTIONS,
  PRESET_LABELS,
  type AccessWizardPreset,
} from '@/lib/access-policy-presets'
import { ApiError } from '@/lib/api'
import type {
  CanonicalWorkspacePrincipal,
  IssuedMcpCredential,
  MeetingArtifactProvider,
  MeetingImportRun,
  MeetingSourceConfig,
  MeetingSourceConnectionState,
  MeetingSourceTestResult,
  UpdateMeetingSourceInput,
} from '@/types/gift-operations'

const PRESETS: AccessWizardPreset[] = ['full', 'numbers-only', 'no-meeting']

const buttonClass =
  'inline-flex min-h-10 items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

function formatDateTime(value: string | null): string {
  if (!value) return '未確認'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '未確認'
  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  }).format(date)
}

function providerLabel(provider: MeetingArtifactProvider): string {
  return provider === 'google_meet' ? 'Google Meet' : 'Zoom Meetings'
}

function QueryUnavailable({ error }: { error: unknown }) {
  const denied = error instanceof ApiError && error.status === 403
  return (
    <div className="rounded-md border border-yellow-600/30 bg-yellow-600/5 p-4 text-sm">
      <p className="font-medium">{denied ? '管理権限がありません' : '未接続'}</p>
      <p className="mt-1 text-muted-foreground">
        {denied
          ? 'manager権限とworkspace所属を確認してください。'
          : 'この管理APIの状態を確認できません。存在しない、または0件とは扱いません。'}
      </p>
    </div>
  )
}

function MutationError({
  error,
  conflictMessage = '操作対象の状態が変更されました。最新状態を確認してから再実行してください。',
}: {
  error: unknown
  conflictMessage?: string
}) {
  if (!error) return null
  const denied = error instanceof ApiError && error.status === 403
  const conflict = error instanceof ApiError && error.status === 409
  return (
    <p role="alert" className="text-sm text-destructive">
      {denied
        ? 'manager以外はこの操作を実行できません。'
        : conflict
          ? conflictMessage
          : '操作を完了できませんでした。状態を再取得してください。'}
    </p>
  )
}

function OperatorOnboardingError({ error }: { error: unknown }) {
  if (!error) return null
  if (error instanceof ApiError && error.status === 403) {
    return <p role="alert" className="text-sm text-destructive">manager以外は利用者を追加できません。</p>
  }
  const code = error instanceof ApiError && error.status === 422
    && typeof error.data === 'object' && error.data !== null
    ? String((error.data as { code?: unknown }).code ?? '')
    : ''
  const messages: Record<string, string> = {
    operator_identity_missing: '既存のidentity directoryに一致する利用者がありません。Platform Opsへ登録状況を確認してください。',
    operator_identity_duplicate: '同じ認証IDが複数のidentityに一致しています。追加せず、重複を解消してください。',
    operator_data_user_slot_duplicate: '同じuser slotが複数のidentityに割り当てられています。追加せず、重複を解消してください。',
    operator_workspace_mismatch: 'identityのworkspaceが現在のworkspaceと一致しません。追加先を確認してください。',
    operator_active_principal_conflict: '同じidentityが別の有効な利用者またはroleに結び付いています。自動変更せず、状態を確認してください。',
  }
  return (
    <p role="alert" className="text-sm text-destructive">
      {messages[code] ?? '利用者を追加できませんでした。identityとworkspaceの状態を確認してください。'}
    </p>
  )
}

function OperatorsPanel({
  workspaceId,
  onIssued,
}: {
  workspaceId: string
  onIssued: (result: IssuedMcpCredential) => void
}) {
  const operatorsQuery = useGiftOperators(workspaceId)
  const onboardOperator = useOnboardGiftOperator(workspaceId)
  const issueCredential = useIssueGiftMcpCredential(workspaceId)
  const reissueCredential = useReissueGiftMcpCredential(workspaceId)
  const revokeCredential = useRevokeGiftMcpCredential(workspaceId)
  const offboardOperator = useOffboardGiftOperator(workspaceId)
  const operators = Array.isArray(operatorsQuery.data?.operators)
    ? operatorsQuery.data.operators
    : []
  const [operatorId, setOperatorId] = useState('')
  const [profile, setProfile] = useState<AccessWizardPreset>('full')
  const [credentialName, setCredentialName] = useState('')
  const [authSubject, setAuthSubject] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [onboardingStatus, setOnboardingStatus] = useState<string | null>(null)

  const activeOperators = useMemo(
    () => operators.filter(
      (operator) =>
        operator.status === 'active'
        && Array.isArray(operator.conflicts)
        && operator.conflicts.length === 0,
    ),
    [operators],
  )

  async function issue() {
    if (!operatorId || !credentialName.trim()) return
    const result = await issueCredential.mutateAsync({
      operatorId,
      profile,
      name: credentialName.trim(),
    })
    onIssued(result)
    setCredentialName('')
  }

  async function onboard() {
    if (!authSubject.trim() || !displayName.trim()) return
    setOnboardingStatus(null)
    try {
      const result = await onboardOperator.mutateAsync({
        authSubject: authSubject.trim(),
        displayName: displayName.trim(),
      })
      setOnboardingStatus(result.alreadyApplied
        ? '同じ追加要求は既に適用済みです。利用者一覧と監査ログを更新しました。'
        : result.alreadyActive
          ? 'この利用者は既に利用中です。変更はありません。'
          : '利用者をmemberとして追加しました。MCPキーと権限は別途割り当ててください。')
      setAuthSubject('')
      setDisplayName('')
    } catch {
      // React QueryのerrorをOperatorOnboardingErrorで表示する。
    }
  }

  async function reissue(credentialId: string) {
    const result = await reissueCredential.mutateAsync(credentialId)
    onIssued(result)
  }

  function confirmRevoke(credentialId: string) {
    if (window.confirm('このMCPキーを失効します。元に戻せません。')) {
      void revokeCredential.mutateAsync(credentialId)
    }
  }

  function confirmOffboard(operator: CanonicalWorkspacePrincipal) {
    const label = operator.displayName ?? 'この利用者'
    if (window.confirm(`${label}を利用停止し、キーと個人ポリシーを無効化します。`)) {
      void offboardOperator.mutateAsync(operator.id)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" aria-hidden />
            利用者とMCPキー
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            identity、access policy、DB key、監査記録を管理APIの1回の操作で更新します。
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <section aria-labelledby="operator-onboard-title" className="space-y-3 rounded-lg border bg-muted/20 p-4">
          <h3 id="operator-onboard-title" className="font-medium">利用者を追加</h3>
          <p className="text-sm text-muted-foreground">
            既存のidentity directoryに登録済みの認証IDだけをmemberとして有効化します。MCPキーと権限は追加後に別途設定します。
          </p>
          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">認証ID</span>
              <input
                aria-label="追加する認証ID"
                value={authSubject}
                maxLength={200}
                onChange={(event) => setAuthSubject(event.target.value)}
                placeholder="user_..."
                className="min-h-10 rounded-md border bg-background px-3"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">表示名</span>
              <input
                aria-label="追加する表示名"
                value={displayName}
                maxLength={200}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="運用担当者"
                className="min-h-10 rounded-md border bg-background px-3"
              />
            </label>
            <button
              type="button"
              disabled={!authSubject.trim() || !displayName.trim() || onboardOperator.isPending}
              onClick={() => void onboard()}
              className={`${buttonClass} self-end border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              追加
            </button>
          </div>
          {onboardingStatus && <p role="status" className="text-sm text-emerald-700">{onboardingStatus}</p>}
          <OperatorOnboardingError error={onboardOperator.error} />
        </section>

        <section aria-labelledby="credential-issue-title" className="space-y-3 rounded-lg border bg-muted/20 p-4">
          <h3 id="credential-issue-title" className="font-medium">MCPキーを発行</h3>
          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto]">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">対象者</span>
              <select
                value={operatorId}
                onChange={(event) => setOperatorId(event.target.value)}
                className="min-h-10 rounded-md border bg-background px-3"
              >
                <option value="">選択してください</option>
                {activeOperators.map((operator) => (
                  <option key={operator.id} value={operator.sessionUserId}>
                    {operator.displayName ?? '表示名未確認'}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">利用プロファイル</span>
              <select
                value={profile}
                onChange={(event) => setProfile(event.target.value as AccessWizardPreset)}
                className="min-h-10 rounded-md border bg-background px-3"
              >
                {PRESETS.map((preset) => (
                  <option key={preset} value={preset}>{PRESET_LABELS[preset]}</option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">キー名</span>
              <input
                value={credentialName}
                onChange={(event) => setCredentialName(event.target.value)}
                placeholder="業務端末"
                className="min-h-10 rounded-md border bg-background px-3"
              />
            </label>
            <button
              type="button"
              disabled={!operatorId || !credentialName.trim() || issueCredential.isPending}
              onClick={() => void issue()}
              className={`${buttonClass} self-end border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              <KeyRound className="h-4 w-4" aria-hidden />
              発行
            </button>
          </div>
          <p className="text-xs text-muted-foreground">{PRESET_DESCRIPTIONS[profile]}</p>
          <MutationError error={issueCredential.error} />
        </section>

        {operatorsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">利用者を確認しています</p>
        ) : operatorsQuery.isError ? (
          <QueryUnavailable error={operatorsQuery.error} />
        ) : operators.length === 0 ? (
          <p className="text-sm text-muted-foreground">登録済み利用者はありません。</p>
        ) : (
          <div className="grid gap-3">
            {operators.map((operator) => (
              <article key={operator.id} className="rounded-lg border p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium">{operator.displayName ?? '表示名未確認'}</h3>
                      <Badge tone={operator.status === 'active' ? 'success' : operator.status === 'conflict' ? 'destructive' : 'muted'}>
                        {operator.status === 'active' ? '利用中' : operator.status === 'conflict' ? 'identity不一致' : operator.status === 'inactive' ? '停止中' : '未確認'}
                      </Badge>
                      <Badge tone="muted">{operator.role ?? 'role未確認'}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      user slot {operator.dataUserSlot ? '接続済み' : '未接続'} / MCP principal {operator.mcpPrincipalRef ? '接続済み' : '未接続'}
                    </p>
                    {Array.isArray(operator.conflicts) && operator.conflicts.length > 0 && (
                      <p className="mt-2 flex items-center gap-2 text-sm text-destructive">
                        <AlertTriangle className="h-4 w-4" aria-hidden />
                        identity不一致 {operator.conflicts.length}件。解消までmutationは停止します。
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={operator.status !== 'active' || offboardOperator.isPending}
                    onClick={() => confirmOffboard(operator)}
                    className={`${buttonClass} border-destructive/40 text-destructive`}
                  >
                    <CircleOff className="h-4 w-4" aria-hidden />
                    利用停止
                  </button>
                </div>
                <div className="mt-4 space-y-2">
                  {!Array.isArray(operator.credentials) || operator.credentials.length === 0 ? (
                    <p className="text-sm text-muted-foreground">管理APIから確認できるMCPキーはありません。</p>
                  ) : operator.credentials.map((credential) => (
                    <div key={credential.id} className="flex flex-col gap-2 rounded-md bg-muted/40 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm">
                        <p className="font-medium">{credential.name}</p>
                        <p className="text-xs text-muted-foreground">
                          prefix {credential.keyPrefix ?? '未確認'} / 最終利用 {formatDateTime(credential.lastUsedAt)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge tone={credential.status === 'active' ? 'success' : 'muted'}>
                          {credential.status === 'active' ? '有効' : credential.status === 'revoked' ? '失効済み' : credential.status === 'expired' ? '期限切れ' : '未確認'}
                        </Badge>
                        <button
                          type="button"
                          disabled={
                            operator.status !== 'active'
                            || operator.conflicts.length > 0
                            || credential.status !== 'active'
                            || reissueCredential.isPending
                          }
                          onClick={() => void reissue(credential.id)}
                          className={buttonClass}
                        >
                          再発行
                        </button>
                        <button
                          type="button"
                          disabled={
                            operator.status !== 'active'
                            || operator.conflicts.length > 0
                            || credential.status !== 'active'
                            || revokeCredential.isPending
                          }
                          onClick={() => confirmRevoke(credential.id)}
                          className={`${buttonClass} text-destructive`}
                        >
                          失効
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
        <MutationError error={offboardOperator.error ?? reissueCredential.error ?? revokeCredential.error} />
      </CardContent>
    </Card>
  )
}

function listText(values: string[] | undefined): string {
  return Array.isArray(values) ? values.join('\n') : ''
}

function parseList(value: string): string[] {
  return [...new Set(value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean))]
}

function sourceConnectionLabel(state: MeetingSourceConnectionState): string {
  if (state === 'connected') return '接続済み'
  if (state === 'not_configured') return '未設定'
  if (state === 'error') return '接続エラー'
  if (state === 'disabled') return '停止中'
  return '未確認'
}

function SourceSettingsCard({
  source,
  isSaving,
  isTesting,
  isSyncing,
  onSave,
  onTest,
  onSync,
}: {
  source: MeetingSourceConfig
  isSaving: boolean
  isTesting: boolean
  isSyncing: boolean
  onSave: (input: UpdateMeetingSourceInput) => Promise<unknown>
  onTest: () => Promise<MeetingSourceTestResult>
  onSync: () => void
}) {
  const [organizerRefs, setOrganizerRefs] = useState(listText(source.organizerRefs))
  const [meetingIds, setMeetingIds] = useState(listText(source.meetingFilter.externalMeetingIds))
  const [artifactIds, setArtifactIds] = useState(listText(source.meetingFilter.externalArtifactIds))
  const [testReference, setTestReference] = useState(
    source.provider === 'google_meet'
      ? source.meetingFilter.conferenceRecord ?? ''
      : source.meetingFilter.meetingId ?? '',
  )
  const [freshness, setFreshness] = useState(String(source.freshnessSlaMinutes))
  const [enabled, setEnabled] = useState(source.enabled)
  const [testResult, setTestResult] = useState<MeetingSourceTestResult | null>(null)

  useEffect(() => {
    setOrganizerRefs(listText(source.organizerRefs))
    setMeetingIds(listText(source.meetingFilter.externalMeetingIds))
    setArtifactIds(listText(source.meetingFilter.externalArtifactIds))
    setTestReference(source.provider === 'google_meet'
      ? source.meetingFilter.conferenceRecord ?? ''
      : source.meetingFilter.meetingId ?? '')
    setFreshness(String(source.freshnessSlaMinutes))
    setEnabled(source.enabled)
    setTestResult(null)
  }, [source])

  const input = useMemo<UpdateMeetingSourceInput>(() => {
    const meetingFilter: UpdateMeetingSourceInput['meetingFilter'] = {
      externalMeetingIds: parseList(meetingIds),
      externalArtifactIds: parseList(artifactIds),
    }
    if (testReference.trim()) {
      if (source.provider === 'google_meet') meetingFilter.conferenceRecord = testReference.trim()
      else meetingFilter.meetingId = testReference.trim()
    }
    return {
      provider: source.provider,
      organizerRefs: parseList(organizerRefs),
      meetingFilter,
      freshnessSlaMinutes: Number(freshness),
      enabled,
    }
  }, [artifactIds, enabled, freshness, meetingIds, organizerRefs, source.provider, testReference])

  const savedInput = useMemo<UpdateMeetingSourceInput>(() => {
    const meetingFilter: UpdateMeetingSourceInput['meetingFilter'] = {
      externalMeetingIds: source.meetingFilter.externalMeetingIds ?? [],
      externalArtifactIds: source.meetingFilter.externalArtifactIds ?? [],
    }
    if (source.provider === 'google_meet' && source.meetingFilter.conferenceRecord) {
      meetingFilter.conferenceRecord = source.meetingFilter.conferenceRecord
    }
    if (source.provider === 'zoom_meeting' && source.meetingFilter.meetingId) {
      meetingFilter.meetingId = source.meetingFilter.meetingId
    }
    return {
      provider: source.provider,
      organizerRefs: source.organizerRefs,
      meetingFilter,
      freshnessSlaMinutes: source.freshnessSlaMinutes,
      enabled: source.enabled,
    }
  }, [source])
  const dirty = JSON.stringify(input) !== JSON.stringify(savedInput)
  const freshnessValid = Number.isInteger(input.freshnessSlaMinutes)
    && input.freshnessSlaMinutes >= 1
    && input.freshnessSlaMinutes <= 10_080
  const visibleConnectionState = testResult?.connectionState ?? source.connectionState
  const testReferenceLabel = source.provider === 'google_meet'
    ? '接続テスト用 conferenceRecord'
    : '接続テスト用 Zoom meetingId'

  async function testSavedSettings() {
    try {
      const result = await onTest()
      setTestResult(result)
    } catch {
      // React Query exposes the error through MutationError below.
    }
  }

  return (
    <article className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-medium">{providerLabel(source.provider)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            最終成功 {formatDateTime(source.lastSucceededAt)}
          </p>
        </div>
        <Badge tone={visibleConnectionState === 'connected' ? 'success' : visibleConnectionState === 'error' ? 'destructive' : 'warning'}>
          {sourceConnectionLabel(visibleConnectionState)}
        </Badge>
      </div>

      <div className="mt-4 grid gap-3">
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">
            {source.provider === 'google_meet' ? '対象 organizer' : '対象 host'}
          </span>
          <textarea
            aria-label={source.provider === 'google_meet' ? '対象 organizer' : '対象 host'}
            value={organizerRefs}
            onChange={(event) => setOrganizerRefs(event.target.value)}
            rows={2}
            className="rounded-md border bg-background px-3 py-2"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">対象会議ID</span>
          <textarea
            aria-label="対象会議ID"
            value={meetingIds}
            onChange={(event) => setMeetingIds(event.target.value)}
            rows={2}
            className="rounded-md border bg-background px-3 py-2"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">対象artifact ID</span>
          <textarea
            aria-label="対象artifact ID"
            value={artifactIds}
            onChange={(event) => setArtifactIds(event.target.value)}
            rows={2}
            className="rounded-md border bg-background px-3 py-2"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-muted-foreground">{testReferenceLabel}</span>
          <input
            aria-label={testReferenceLabel}
            value={testReference}
            onChange={(event) => setTestReference(event.target.value)}
            className="min-h-10 rounded-md border bg-background px-3"
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="grid gap-1 text-sm">
            <span className="text-muted-foreground">鮮度基準（分）</span>
            <input
              aria-label="鮮度基準（分）"
              type="number"
              min={1}
              max={10_080}
              value={freshness}
              onChange={(event) => setFreshness(event.target.value)}
              className="min-h-10 rounded-md border bg-background px-3"
            />
          </label>
          <label className="flex min-h-10 items-center gap-2 self-end text-sm">
            <input
              aria-label="自動同期を有効化"
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            自動同期を有効化
          </label>
        </div>
      </div>

      <dl className="mt-4 grid gap-2 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">認証情報</dt>
          <dd>{source.credentialState === 'configured' ? '参照設定済み（値は非表示）' : '未確認'}</dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={isSaving || !dirty || !freshnessValid}
          onClick={() => void onSave(input).catch(() => undefined)}
          className={buttonClass}
        >
          設定を保存
        </button>
        <button
          type="button"
          disabled={isTesting || dirty || !testReference.trim()}
          onClick={() => void testSavedSettings()}
          className={buttonClass}
        >
          接続テスト
        </button>
        <button
          type="button"
          disabled={
            !source.enabled
            || isSyncing
            || !source.meetingFilter.externalMeetingIds?.[0]
            || !source.meetingFilter.externalArtifactIds?.[0]
          }
          onClick={onSync}
          className={buttonClass}
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
          今すぐ同期
        </button>
      </div>
      {dirty && <p className="mt-2 text-xs text-muted-foreground">変更を保存すると接続テストできます。</p>}
      {testResult && (
        <p role="status" className={`mt-2 text-sm ${testResult.ok ? 'text-emerald-700' : 'text-destructive'}`}>
          接続テスト {testResult.ok ? '成功' : '失敗'}（{testResult.code}）
        </p>
      )}
    </article>
  )
}

export function SourcesPanel({ workspaceId }: { workspaceId: string }) {
  const sourcesQuery = useMeetingSources(workspaceId)
  const updateSource = useUpdateMeetingSource(workspaceId)
  const testSource = useTestMeetingSource(workspaceId)
  const syncImports = useSyncMeetingImports(workspaceId)
  const sources = Array.isArray(sourcesQuery.data?.sources) ? sourcesQuery.data.sources : []

  function syncSource(source: MeetingSourceConfig) {
    const meetings = source.meetingFilter.externalMeetingIds
    const artifacts = source.meetingFilter.externalArtifactIds
    const externalMeetingId = Array.isArray(meetings) ? meetings[0] : undefined
    const externalArtifactId = Array.isArray(artifacts) ? artifacts[0] : undefined
    if (!externalMeetingId || !externalArtifactId) return
    void syncImports.mutateAsync({
      sourceConfigId: source.id,
      provider: source.provider,
      externalMeetingId,
      externalArtifactId,
    }).catch(() => undefined)
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" aria-hidden />
            会議連携
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            secretを表示せず、Google MeetとZoom Meetingsの接続、対象、同期状態を管理します。
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {sourcesQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">会議連携を確認しています</p>
        ) : sourcesQuery.isError ? (
          <QueryUnavailable error={sourcesQuery.error} />
        ) : sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">会議連携は登録されていません。</p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {sources.map((source) => (
              <SourceSettingsCard
                key={source.id}
                source={source}
                isSaving={updateSource.isPending}
                isTesting={testSource.isPending}
                isSyncing={syncImports.isPending}
                onSave={(input) => updateSource.mutateAsync({ sourceId: source.id, input })}
                onTest={() => testSource.mutateAsync(source.id)}
                onSync={() => syncSource(source)}
              />
            ))}
          </div>
        )}
        <div className="mt-3">
          <MutationError error={updateSource.error ?? testSource.error ?? syncImports.error} />
        </div>
      </CardContent>
    </Card>
  )
}

function ImportReviewPanel({ workspaceId }: { workspaceId: string }) {
  const [targetWorkstreamByRun, setTargetWorkstreamByRun] = useState<Record<string, string>>({})
  const importsQuery = useMeetingImports(workspaceId)
  const approvalTargetQuery = useMeetingImportApprovalTarget(workspaceId)
  const approveImport = useApproveMeetingImport(workspaceId)
  const rejectImport = useRejectMeetingImport(workspaceId)
  const imports = Array.isArray(importsQuery.data?.imports) ? importsQuery.data.imports : []
  const reviewRequired = imports.filter((item) => item.state === 'review_required')

  function confirmDecision(run: MeetingImportRun, decision: 'approve' | 'reject') {
    const verb = decision === 'approve' ? '承認し、新しい週次revisionへの反映を要求' : '却下'
    if (!window.confirm(`この更新候補を${verb}します。`)) return
    if (decision === 'approve') {
      const target = approvalTargetQuery.data
      if (!target) return
      const targetWorkstreamId = targetWorkstreamByRun[run.id]
      if (!targetWorkstreamId) return
      void approveImport.mutateAsync({
        importId: run.id,
        expectedWeekOf: target.weekOf,
        expectedSnapshotId: target.snapshotId,
        expectedRevision: target.revision,
        targetWorkstreamId,
      })
    } else {
      void rejectImport.mutateAsync(run.id)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
            会議からの更新候補
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            承認前は週次snapshotを変更しません。文字起こし全文は表示、保存しません。
          </p>
        </div>
        <Badge tone={reviewRequired.length > 0 ? 'warning' : 'muted'}>
          確認待ち {reviewRequired.length}件
        </Badge>
      </CardHeader>
      <CardContent>
        {importsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">更新候補を確認しています</p>
        ) : importsQuery.isError ? (
          <QueryUnavailable error={importsQuery.error} />
        ) : imports.length === 0 ? (
          <p className="text-sm text-muted-foreground">更新候補はありません。</p>
        ) : (
          <div className="grid gap-3">
            {imports.map((run) => (
              <article key={run.id} className="rounded-lg border p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium">{providerLabel(run.provider)}</h3>
                      <Badge tone={run.state === 'review_required' ? 'warning' : run.state === 'approved' ? 'success' : run.state === 'failed' ? 'destructive' : 'muted'}>
                        {run.state === 'review_required' ? '確認待ち' : run.state === 'approved' ? '承認済み' : run.state === 'rejected' ? '却下済み' : run.state === 'retryable' ? '再試行待ち' : run.state === 'failed' ? '取得エラー' : run.state === 'deduplicated' ? '重複解決済み' : '処理中'}
                      </Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      検出 {formatDateTime(run.detectedAt)} / reason {run.reasonCode ?? 'なし'}
                    </p>
                  </div>
                  {run.state === 'review_required' && (
                    <div className="flex flex-wrap gap-2">
                      <label className="sr-only" htmlFor={`target-workstream-${run.id}`}>反映先プロジェクト</label>
                      <select
                        id={`target-workstream-${run.id}`}
                        value={targetWorkstreamByRun[run.id] ?? ''}
                        onChange={(event) => setTargetWorkstreamByRun((current) => ({
                          ...current,
                          [run.id]: event.target.value,
                        }))}
                        className="min-h-10 rounded-md border bg-background px-3 text-sm"
                      >
                        <option value="">反映先を選択</option>
                        {(approvalTargetQuery.data?.workstreams ?? []).map((workstream) => (
                          <option key={workstream.workstreamId} value={workstream.workstreamId}>
                            {workstream.title}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={approveImport.isPending || rejectImport.isPending}
                        onClick={() => confirmDecision(run, 'reject')}
                        className={`${buttonClass} text-destructive`}
                      >
                        却下
                      </button>
                      <button
                        type="button"
                        disabled={approveImport.isPending || rejectImport.isPending
                          || !approvalTargetQuery.data
                          || approvalTargetQuery.isFetching
                          || !targetWorkstreamByRun[run.id]
                        }
                        onClick={() => confirmDecision(run, 'approve')}
                        className={`${buttonClass} border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
                      >
                        承認
                      </button>
                    </div>
                  )}
                </div>
                <ul className="mt-4 space-y-2">
                  {!Array.isArray(run.candidates) || run.candidates.length === 0 ? (
                    <li className="text-sm text-muted-foreground">抽出候補は未確認です。</li>
                  ) : run.candidates.map((candidate) => (
                    <li key={candidate.id} className="rounded-md bg-muted/40 p-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone="muted">{candidate.kind}</Badge>
                        <span>{candidate.summary}</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        confidence {candidate.confidence === null ? '未確認' : candidate.confidence.toFixed(2)}
                      </p>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        )}
        <div className="mt-3">
          <MutationError
            error={approveImport.error ?? rejectImport.error}
            conflictMessage="週次revisionが変更されました。最新状態と反映先を確認してから、明示的に再承認してください。"
          />
        </div>
      </CardContent>
    </Card>
  )
}

function AuditPanel({ workspaceId }: { workspaceId: string }) {
  const auditQuery = useGiftAuditEvents(workspaceId)
  const events = Array.isArray(auditQuery.data?.events) ? auditQuery.data.events : []

  return (
    <Card>
      <CardHeader>
        <CardTitle>操作履歴</CardTitle>
      </CardHeader>
      <CardContent>
        {auditQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">操作履歴を確認しています</p>
        ) : auditQuery.isError ? (
          <QueryUnavailable error={auditQuery.error} />
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">記録された操作はありません。</p>
        ) : (
          <ol className="divide-y">
            {events.map((event) => (
              <li key={event.id} className="grid gap-1 py-3 text-sm sm:grid-cols-[10rem_1fr]">
                <time className="tabular-nums text-muted-foreground">{formatDateTime(event.occurredAt)}</time>
                <div>
                  <p className="font-medium">{event.action}</p>
                  <p className="text-muted-foreground">
                    {event.actorLabel ?? '実行者未確認'} → {event.targetLabel ?? '対象未確認'}
                  </p>
                  {event.summary && <p className="mt-1">{event.summary}</p>}
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function IssuedCredentialModal({
  result,
  onClose,
}: {
  result: IssuedMcpCredential | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.rawKey)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Modal open={result !== null} onOpenChange={(open) => !open && onClose()}>
      <ModalHeader>
        <ModalTitle>MCPキーを発行しました</ModalTitle>
        <ModalDescription>平文キーはこの画面を閉じると再表示できません。</ModalDescription>
      </ModalHeader>
      <div className="rounded-md border bg-muted/40 p-3">
        <code className="block break-all text-sm">{result?.rawKey}</code>
      </div>
      <ModalFooter>
        <button type="button" onClick={() => void copy()} className={buttonClass}>
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? 'コピー済み' : 'キーをコピー'}
        </button>
        <button type="button" onClick={onClose} className={`${buttonClass} bg-primary text-primary-foreground`}>
          閉じる
        </button>
      </ModalFooter>
    </Modal>
  )
}

export function GiftOperationsPage() {
  const [issuedCredential, setIssuedCredential] = useState<IssuedMcpCredential | null>(null)
  const workspace = useResolvedWorkspace()

  if (workspace.isLoading) {
    return <p className="text-sm text-muted-foreground">対象workspaceを確認しています</p>
  }
  if (!workspace.workspaceId) {
    return <QueryUnavailable error={workspace.error} />
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] space-y-6">
      <header className="border-b pb-5">
        <h1 className="text-2xl font-bold">MCP・連携管理</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          日常運用だけを共同管理します。SSH、デプロイ、DB migration、OAuth secretはPlatform Opsの管理対象です。
        </p>
      </header>
      <OperatorsPanel workspaceId={workspace.workspaceId} onIssued={setIssuedCredential} />
      <SourcesPanel workspaceId={workspace.workspaceId} />
      <ImportReviewPanel workspaceId={workspace.workspaceId} />
      <AuditPanel workspaceId={workspace.workspaceId} />
      <IssuedCredentialModal
        result={issuedCredential}
        onClose={() => setIssuedCredential(null)}
      />
    </div>
  )
}
